import { CloseOutlined } from "@ant-design/icons";
import CustomSelect from "@common/CustomSelect";
import Drawer from "@common/Drawer";
import { useState } from "react";
import selectArrowIcon from "@assets/icons/select_arrow.svg";
import Upload from "@common/Upload";
import { PrimaryButton, SecondaryButton } from "@common/Buttons";
import cloudUploadIcon from "@assets/icons/cloud_upload.svg";
import FileUploadSuccess from "./FileUploadSuccess";
import { useFileUpload } from "@hooks/useFileUpload";
import { objectToFormData } from "@store/utilities";
import { uploadBaseUrl } from "@store/urlPaths";
import ProgressText from "@common/ProgressText";
import { useNavigate } from "react-router-dom";
import { getCurrentYearAndMonth } from "@utils/helpers";

const currentYearAndMonth = getCurrentYearAndMonth();

export default function UploadDrawer({
  isOpen,
  onClose,
  months,
  years,
  drawerInputsToShow = [],
  url,
  activeTab,
  saveAndGenerateUrls,
  redirectUrl,
  products,
  useDefaultSheetNames,
  previewSelectInputFields,
  previewLockInputFields,
  fetchProductsWiseData
}) {
  const [files, setFiles] = useState([]);
  const [year, setYear] = useState(currentYearAndMonth.year);
  const [month, setMonth] = useState(currentYearAndMonth.month);
  const [product, setProduct] = useState(null);
  const navigate = useNavigate();
  const { uploadFile, progress, isUploading, error } = useFileUpload(
    `${uploadBaseUrl}/${url}`
  );

  const resetComp = () => {
    setFiles([]);
    setMonth(currentYearAndMonth.month);
    setProduct(null);
    setYear(currentYearAndMonth.year);
  };

  //removing files from files array state
  const handelRemoveFiles = () => {
    setFiles([]);
  };

  //handle click on close
  const handleClose = () => {
    onClose();
    resetComp();
  };

  //uploading file
  const handleUploadFile = async () => {
    const payload = {
      month,
      year,
      file: files,
    };

    if (drawerInputsToShow.includes("product")) {
      payload.productName = String(product).toLowerCase();
    }

    const response = await uploadFile(objectToFormData(payload));
    if (response.success) {
      navigate(`/preview?tab=${activeTab}`, {
        state: {
          data: response.data || response,
          saveAndGenerateUrls,
          redirectUrl,
          useDefaultSheetNames,
          previewSelectInputFields,
          previewLockInputFields,
          fetchProductsWiseData
        },
      });
    }
  };

  return (
    <Drawer open={isOpen} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {/* header */}
        <div className="flex justify-between items-center">
          <label className="text-base font-semibold text-primary-tint">
            Upload Files
          </label>
          <CloseOutlined
            className="!text-text-tint !h-4 cursor-pointer !w-4"
            onClick={handleClose}
          />
        </div>

        {/* input fields */}
        <div className="flex flex-col gap-2.5">
          {drawerInputsToShow.includes("year") ? (
            <CustomSelect
              label="Year"
              onChange={setYear}
              value={year}
              options={years}
              className="[&_.ant-select-selector]:!h-9 [&_.ant-select-selection-item]:!text-text-main [&_.ant-select-selection-item]:!text-xs"
              variant="outlined"
              icon={selectArrowIcon}
              placeholder="Select year"
            />
          ) : null}
          {drawerInputsToShow.includes("month") ? (
            <CustomSelect
              label="Month"
              onChange={setMonth}
              value={month}
              options={months}
              className="[&_.ant-select-selector]:!h-9 [&_.ant-select-selection-item]:!text-text-main [&_.ant-select-selection-item]:!text-xs"
              variant="outlined"
              icon={selectArrowIcon}
              placeholder="Select month"
            />
          ) : null}
          {drawerInputsToShow.includes("product") ? (
            <CustomSelect
              label="Product"
              onChange={setProduct}
              value={product}
              options={products}
              className="[&_.ant-select-selector]:!h-9 [&_.ant-select-selection-item]:!text-text-main [&_.ant-select-selection-item]:!text-xs"
              variant="outlined"
              icon={selectArrowIcon}
              placeholder="Select product"
              showSearch
            />
          ) : null}

          {/* upload */}
          {files.length ? (
            <div className="grid grid-cols-2 mt-6">
              <FileUploadSuccess
                onDelete={handelRemoveFiles}
                filename={files[0].name}
              />
            </div>
          ) : (
            <Upload
              allowedFileTypes={[".xlsx"]}
              setFiles={setFiles}
              files={files}
              limit={1}
            >
              <div className="px-4 cursor-pointer rounded-lg py-4 justify-center items-center border border-dashed border-[#D0D5DD] flex flex-col gap-1">
                <img
                  alt="upload"
                  src={cloudUploadIcon}
                  className="!h-7 !w-7 mb-2 cursor-pointer"
                />
                <label className="text-sm font-medium cursor-pointer text-primary-tint">
                  Click to upload
                </label>
                <span className="text-xs font-medium cursor-pointer text-text-tint">
                  only .xlx file allowed
                </span>
              </div>
            </Upload>
          )}
        </div>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}
        {/* action buttons */}
        <div className="flex gap-4 justify-end items-center">
          <SecondaryButton disabled={isUploading} onClick={handleClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            disabled={!files.length}
            loading={isUploading}
            onClick={handleUploadFile}
          >
            Upload
          </PrimaryButton>
        </div>

        {/* progress */}
        {isUploading ? (
          <div className="flex flex-col gap-2 justify-center items-center mt-4">
            <ProgressText
              progress={progress}
              progressText="Uploading in progress..."
            />

            {isUploading && progress === 100 ? (
              <ProgressText
                progress={isUploading}
                progressText="Processing your file..."
              />
            ) : null}
          </div>
        ) : null}

        {/* preview data */}
        {/* {previewData ? <Preview data={previewData} /> : null} */}
      </div>
    </Drawer>
  );
}
